import React, { lazy } from "react";
import type { RouteObject } from "react-router-dom";

// Import all page components using Vite's glob import
const pages = import.meta.glob("/src/app/**/page.tsx", { eager: false });
const layouts = import.meta.glob("/src/app/**/layout.tsx", { eager: false });
const notFound = import.meta.glob("/src/app/not-found.tsx", { eager: false });
const errorPages = import.meta.glob("/src/app/**/error.tsx", { eager: false });

interface RouteInfo {
  path: string;
  component: () => Promise<{ default: React.ComponentType<any> }>;
  layouts?: Array<() => Promise<{ default: React.ComponentType<any> }>>;
  error?: () => Promise<{ default: React.ComponentType<any> }>;
  children?: RouteInfo[];
}

// Convert file path to route path
function filePathToRoutePath(filePath: string): string {
  // Remove /src/app prefix
  let routePath = filePath.replace(/^\/src\/app/, "");

  // Remove /page.tsx suffix
  routePath = routePath.replace(/\/page\.tsx$/, "");

  // Remove route groups (parentheses) - must be done before checking for empty
  routePath = routePath.replace(/\([^)]+\)/g, "");

  // Handle index routes
  if (routePath === "" || routePath === "/") {
    return "/";
  }

  // Handle dynamic routes [id] -> :id
  routePath = routePath.replace(/\[([^\]]+)\]/g, ":$1");

  // Handle catch-all routes [...slug] -> *
  routePath = routePath.replace(/\[\.\.\.([^\]]+)\]/g, "*");

  // Clean up double slashes
  routePath = routePath.replace(/\/+/g, "/");

  // Ensure it starts with /
  if (!routePath.startsWith("/")) {
    routePath = "/" + routePath;
  }

  return routePath;
}

// Find all layouts for a route based on the file path (from root to closest)
function findLayoutsForRoute(filePath: string): string[] {
  // Extract directory path from file path
  const dirPath = filePath.replace(/\/page\.tsx$/, "");

  // Build all possible layout paths from root to closest
  const allPaths: string[] = [];

  // Start from root and build up to the page directory
  allPaths.push("/src/app/layout.tsx");

  // Build paths from root to page directory
  const pathParts = dirPath
    .replace(/^\/src\/app/, "")
    .split("/")
    .filter(Boolean);

  let currentPath = "/src/app";
  for (const part of pathParts) {
    currentPath = `${currentPath}/${part}`;
    allPaths.push(`${currentPath}/layout.tsx`);
  }

  // Find all existing layouts (from root to closest)
  const foundLayouts: string[] = [];
  for (const testPath of allPaths) {
    if (layouts[testPath]) {
      foundLayouts.push(testPath);
    }
  }

  return foundLayouts;
}

// Build route tree
function buildRouteTree(): RouteInfo[] {
  const routeMap = new Map<string, RouteInfo>();

  // Process all pages
  Object.keys(pages).forEach((filePath) => {
    const routePath = filePathToRoutePath(filePath);
    const layoutPaths = findLayoutsForRoute(filePath);

    const routeInfo: RouteInfo = {
      path: routePath,
      component: pages[filePath] as () => Promise<{ default: React.ComponentType<any> }>,
      layouts:
        layoutPaths.length > 0
          ? layoutPaths.map((layoutPath) => layouts[layoutPath] as () => Promise<{ default: React.ComponentType<any> }>)
          : undefined,
    };

    // Check for error boundary
    const errorPath = filePath.replace(/\/page\.tsx$/, "/error.tsx");
    if (errorPages[errorPath]) {
      routeInfo.error = errorPages[errorPath] as () => Promise<{ default: React.ComponentType<any> }>;
    }

    routeMap.set(routePath, routeInfo);
  });

  // Convert to array and sort by path depth (deeper paths first)
  const routes = Array.from(routeMap.values()).sort((a, b) => {
    const depthA = a.path.split("/").length;
    const depthB = b.path.split("/").length;
    return depthB - depthA;
  });

  return routes;
}

// Convert RouteInfo to React Router RouteObject
function routeInfoToRouteObject(routeInfo: RouteInfo): RouteObject {
  const LazyComponent = lazy(routeInfo.component);
  const LazyError = routeInfo.error ? lazy(routeInfo.error) : null;

  // Create element with nested layout wrappers if layouts exist
  // Layouts are applied from root to closest (outermost to innermost)
  let element: React.ReactElement = <LazyComponent />;

  if (routeInfo.layouts && routeInfo.layouts.length > 0) {
    // Layouts array is ordered from root to closest (outermost to innermost)
    // But we need to wrap from innermost (closest) to outermost (root)
    // So we reverse the array and wrap: management wraps page first, then private wraps that, then root wraps that
    const layoutComponents = routeInfo.layouts
      .slice()
      .reverse()
      .map((layout) => lazy(layout));

    // Start with the page component
    // Then wrap with each layout from closest (innermost) to root (outermost)
    element = layoutComponents.reduce(
      (acc, LazyLayout) => <LazyLayout>{acc}</LazyLayout>,
      (<LazyComponent />) as React.ReactElement
    );
  }

  const routeObject: RouteObject = {
    path: routeInfo.path,
    element,
    errorElement: LazyError ? <LazyError /> : undefined,
  };

  return routeObject;
}

// Generate all routes
export function generateRoutes(): RouteObject[] {
  const routeTree = buildRouteTree();
  const routes = routeTree.map(routeInfoToRouteObject);

  // Add not-found route
  if (Object.keys(notFound).length > 0) {
    const NotFoundComponent = lazy(
      notFound[Object.keys(notFound)[0]] as () => Promise<{ default: React.ComponentType<any> }>
    );
    routes.push({
      path: "*",
      element: <NotFoundComponent />,
    });
  }

  return routes;
}
